/**
 * ===============================================================
 * priceAuditor.js
 * ===============================================================
 *
 * TEMPLATE MAINTENEUR — GUIDE MANUEL
 * ---------------------------------------------------------------
 * RÔLE DE CE MODULE
 * ---------------------------------------------------------------
 * Ce fichier est la source de vérité FRONT pour la logique pricing.
 *
 * Il doit couvrir 2 cas métier distincts :
 *
 * 1) TARIFICATION MEMBRE / CONNECTÉ
 *    - visiteur voit le prix public
 *    - utilisateur connecté voit le prix membre
 *
 * 2) PROMOTION CLASSIQUE
 *    - old_price = prix de référence / barré
 *    - price = prix réduit
 *    - isDiscount active la promo marketing
 *
 * IMPORTANT
 * ---------------------------------------------------------------
 * Dans Marché Deluxe, ces 2 cas ne doivent PAS être confondus.
 *
 * Exemple critique du bug traité :
 * - prix visiteur : 9 CHF
 * - prix connecté : 7 CHF
 *
 * Si on traite cela seulement comme une "promo",
 * le panier peut casser après login car :
 * - le cart item peut avoir price=900 stocké
 * - mais memberPrice=700 doit rester exploitable
 *
 * Ce fichier doit donc :
 * - tolérer les données legacy
 * - relire plusieurs champs possibles
 * - toujours renvoyer des CENTIMES
 * - rester compatible avec l’existant
 *
 * CONTRAT DE SORTIE
 * ---------------------------------------------------------------
 * Le résultat doit rester stable pour les composants existants :
 *
 * - pricingMode
 * - availabilityStatus
 * - displayPrice
 * - displayBasePrice
 * - displaySavingsTag
 * - isPromoActive
 * - hasPriceAdvantage
 * - hasUpsell
 * - marketingIntent
 * - basePrice
 * - targetPrice
 * - discountLabel
 *
 * + ALIAS DE COMPATIBILITÉ
 * - publicPrice
 * - memberPrice
 * - oldPrice
 * - displayUpsell
 *
 * ===============================================================
 */

/**
 * ---------------------------------------------------------------
 * HELPER — NORMALISATION CENTIMES
 * ---------------------------------------------------------------
 *
 * OBJECTIF
 * - accepter int / float / string
 * - toujours ressortir un entier centimes
 * - rester tolérant au legacy
 *
 * RÈGLE
 * - si valeur >= 100 et entière, on considère déjà des centimes
 * - sinon, si décimal petit, on convertit CHF -> centimes
 *
 * EXEMPLES
 * - 900    -> 900
 * - "900"  -> 900
 * - 9      -> 900
 * - 9.00   -> 900
 * - "7.5"  -> 750
 */
const toCents = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;

  // -------------------------------------------------------------
  // Cas entier "important" => on suppose déjà des centimes
  // -------------------------------------------------------------
  if (Number.isInteger(n) && n >= 100) {
    return Math.round(n);
  }

  // -------------------------------------------------------------
  // Cas petit entier ou décimal => probablement CHF
  // -------------------------------------------------------------
  return Math.round(n * 100);
};

/**
 * ---------------------------------------------------------------
 * HELPER — PREMIÈRE VALEUR CENTIMES VALIDE
 * ---------------------------------------------------------------
 */
const firstValidCents = (...values) => {
  for (const value of values) {
    const cents = toCents(value);
    if (cents !== null && cents > 0) return cents;
  }
  return null;
};

/**
 * ---------------------------------------------------------------
 * HELPER — STATUT CONNECTÉ / MEMBRE
 * ---------------------------------------------------------------
 *
 * OBJECTIF
 * Déterminer si l’utilisateur courant peut recevoir le tarif membre.
 *
 * IMPORTANT
 * On reste tolérant :
 * - id
 * - uid
 * - email
 * - rôle connu
 */
const isConnectedMember = (profilPricing, userProfil) => {
  const hasIdentity = !!(
    userProfil?.id ||
    userProfil?.uid ||
    userProfil?.email ||
    userProfil?.prenom
  );

  const type = String(
    userProfil?.type_profil || profilPricing?.type_profil || ""
  )
    .trim()
    .toLowerCase();

  const isKnownMemberType =
    type === "client" ||
    type === "admin" ||
    type === "collaborateur" ||
    type === "collaborator";

  const isPrivileged =
    !!profilPricing?.isAdmin || !!profilPricing?.isCollaborator;

  return hasIdentity && (isKnownMemberType || isPrivileged || !type);
};

/**
 * ---------------------------------------------------------------
 * HELPER — DISPONIBILITÉ PRODUIT
 * ---------------------------------------------------------------
 */
const readAvailability = (produit) => {
  const isOnline = !(
    produit?.isOnline === false ||
    produit?.isOnline === 0 ||
    produit?.isOnline === "0"
  );

  const stock = Number(produit?.stock ?? 0) || 0;

  let availabilityStatus = "AVAILABLE";

  if (!isOnline) {
    availabilityStatus = "DISABLED";
  } else if (stock <= 0) {
    availabilityStatus = "OUT_OF_STOCK";
  }

  return { isOnline, stock, availabilityStatus };
};

/**
 * ===============================================================
 * FONCTION PRINCIPALE
 * ===============================================================
 *
 * SIGNATURE LEGACY CONSERVÉE
 * ---------------------------------------------------------------
 * Compat assurée avec :
 * - auditPrice(produit, { profilPricing, userProfil })
 * - auditPrice(produit, profilPricing, userProfil)
 */
export const auditPrice = (produit, arg2 = {}, arg3 = null) => {
  // -------------------------------------------------------------
  // BRIDGE DE RÉTROCOMPATIBILITÉ
  // -------------------------------------------------------------
  let profilPricing = arg2;
  let userProfil = arg3;

  if (
    arg2 &&
    typeof arg2 === "object" &&
    ("profilPricing" in arg2 || "userProfil" in arg2)
  ) {
    profilPricing = arg2.profilPricing || {};
    userProfil = arg2.userProfil || null;
  }

  // -------------------------------------------------------------
  // FILET DE SÉCURITÉ
  // -------------------------------------------------------------
  if (!produit || typeof produit !== "object") {
    return {
      pricingMode: "PUBLIC",
      availabilityStatus: "HIDDEN",
      displayPrice: 0,
      displayBasePrice: null,
      displaySavingsTag: null,
      displayUpsell: null,
      isPromoActive: false,
      hasPriceAdvantage: false,
      hasUpsell: false,
      marketingIntent: "NONE",
      basePrice: 0,
      targetPrice: 0,
      publicPrice: 0,
      memberPrice: 0,
      oldPrice: null,
      discountLabel: null,
    };
  }

  // =============================================================
  // 1) DISPONIBILITÉ
  // =============================================================
  const { availabilityStatus } = readAvailability(produit);

  // =============================================================
  // 2) LECTURE DES SOURCES DE PRIX
  // =============================================================
  /**
   * IMPORTANT
   * -------------------------------------------------------------
   * On lit plusieurs familles de champs pour rester compatible :
   *
   * PRIX PUBLIC / VISITEUR
   * - publicPrice
   * - prix_visiteur
   * - prix_public
   * - basePrice
   * - displayBasePrice
   * - old_price
   * - oldPrice
   * - public_price
   * - price
   *
   * PRIX MEMBRE / CONNECTÉ
   * - memberPrice
   * - prix_connecte
   * - prix_membre
   * - pricePro
   * - targetPrice
   *
   * PRIX COURANT / APPLIQUÉ
   * - displayPrice
   * - appliedPrice
   * - price
   */
  const explicitPublicPrice = firstValidCents(
    produit?.publicPrice,
    produit?.prix_visiteur,
    produit?.prix_public,
    produit?.basePrice,
    produit?.displayBasePrice,
    produit?.public_price
  );

  const explicitMemberPrice = firstValidCents(
    produit?.memberPrice,
    produit?.prix_connecte,
    produit?.prix_membre,
    produit?.pricePro,
    produit?.targetPrice
  );

  const currentStoredPrice = firstValidCents(
    produit?.displayPrice,
    produit?.appliedPrice,
    produit?.price,
    produit?.prix
  );

  const oldPriceCandidate = firstValidCents(
    produit?.old_price,
    produit?.oldPrice
  );

  const isDiscount =
    produit?.isDiscount === true ||
    produit?.isDiscount === 1 ||
    produit?.isDiscount === "1";

  // =============================================================
  // 3) DÉTECTION PROMO CLASSIQUE
  // =============================================================
  /**
   * PROMO CLASSIQUE
   * -------------------------------------------------------------
   * On ne considère une vraie promo marketing que si :
   * - isDiscount est actif
   * - old_price > current price
   *
   * Cela protège des vieux champs old_price traînants.
   */
  const legacyPromoActive = Boolean(
    isDiscount &&
      oldPriceCandidate &&
      currentStoredPrice &&
      currentStoredPrice < oldPriceCandidate
  );

  // =============================================================
  // 4) CONSTRUCTION PRIX PUBLIC / PRIX MEMBRE
  // =============================================================
  /**
   * CAS MÉTIER COUVERTS
   * -------------------------------------------------------------
   *
   * A) Si public/member sont explicitement fournis :
   *    on les utilise en priorité
   *
   * B) Sinon, si promo classique détectée :
   *    - public = old_price
   *    - member = current price
   *
   * C) Sinon, fallback safe :
   *    - public = current price
   *    - member = explicitMemberPrice si disponible
   */
  const publicPrice =
    explicitPublicPrice ??
    (legacyPromoActive ? oldPriceCandidate : null) ??
    currentStoredPrice ??
    oldPriceCandidate ??
    0;

  let memberPrice =
    explicitMemberPrice ??
    (legacyPromoActive ? currentStoredPrice : null) ??
    null;

  /**
   * GARDE-FOU
   * -------------------------------------------------------------
   * Si le prix membre n’est pas inférieur au public,
   * on le neutralise comme "pas d’avantage membre réel".
   */
  if (
    memberPrice !== null &&
    publicPrice > 0 &&
    Number(memberPrice) >= Number(publicPrice)
  ) {
    memberPrice = null;
  }

  const hasMemberAdvantage = Boolean(
    publicPrice > 0 && memberPrice !== null && memberPrice < publicPrice
  );

  const connectedMember = isConnectedMember(profilPricing, userProfil);

  // =============================================================
  // 5) MODE PRICING
  // =============================================================
  let pricingMode = "PUBLIC";

  if (hasMemberAdvantage && connectedMember) {
    pricingMode = "MEMBER_ACTIVE";
  } else if (hasMemberAdvantage && !connectedMember) {
    pricingMode = "MEMBER_PREVIEW";
  }

  // =============================================================
  // 6) PRIX AFFICHÉ
  // =============================================================
  /**
   * VISITEUR
   * - voit le prix public
   * - avec upsell si un prix membre existe
   *
   * CONNECTÉ
   * - voit le prix membre si avantage réel
   * - sinon voit le prix public
   */
  let displayPrice = publicPrice;
  let displayBasePrice = null;
  let displaySavingsTag = null;
  let displayUpsell = null;

  const targetPrice = hasMemberAdvantage ? memberPrice : publicPrice;

  const discountPercent =
    hasMemberAdvantage && publicPrice > 0
      ? Math.round(((publicPrice - memberPrice) * 100) / publicPrice)
      : 0;

  const discountLabel =
    hasMemberAdvantage && discountPercent > 0 ? `-${discountPercent}%` : null;

  if (pricingMode === "MEMBER_ACTIVE") {
    displayPrice = memberPrice;
    displayBasePrice = publicPrice;
    displaySavingsTag = discountLabel;
  }

  if (pricingMode === "MEMBER_PREVIEW") {
    displayPrice = publicPrice;
    displayBasePrice = null;
    displaySavingsTag = null;

    displayUpsell = {
      i18nLabel: "memberPriceLabel",
      i18nMessage: "memberUnlockMessage",
      priceValue: memberPrice,
    };
  }

  // =============================================================
  // 7) PROMO MARKETING
  // =============================================================
  /**
   * IMPORTANT
   * -------------------------------------------------------------
   * On garde isPromoActive pour l’existant UI,
   * mais on le réserve au vrai cas promo legacy.
   *
   * En revanche, hasPriceAdvantage devient le vrai signal général :
   * "il existe un prix membre inférieur au public".
   */
  const isPromoActive = legacyPromoActive;

  // =============================================================
  // 8) RÉSULTAT CANONIQUE
  // =============================================================
  return {
    pricingMode,
    availabilityStatus,

    // -----------------------------------------------------------
    // AFFICHAGE UI
    // -----------------------------------------------------------
    displayPrice,
    displayBasePrice,
    displaySavingsTag,
    displayUpsell,

    // -----------------------------------------------------------
    // INTENTIONS MARKETING / UX
    // -----------------------------------------------------------
    isPromoActive,
    hasPriceAdvantage: hasMemberAdvantage,
    hasUpsell: pricingMode === "MEMBER_PREVIEW",
    marketingIntent:
      pricingMode === "MEMBER_PREVIEW" ? "ACCOUNT_UPSELL" : "NONE",

    // -----------------------------------------------------------
    // VALEURS CANONIQUES (CENTIMES)
    // -----------------------------------------------------------
    basePrice: publicPrice,
    targetPrice,
    publicPrice,
    memberPrice: memberPrice ?? publicPrice,
    oldPrice: displayBasePrice ?? oldPriceCandidate ?? null,

    // -----------------------------------------------------------
    // LABELS / AIDES
    // -----------------------------------------------------------
    discountLabel,
  };
};
